import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { FilterState } from "../types/filters";
import type { GraphData } from "../types/graph";

export interface FilterContextValue {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  resetFilters: () => void;
  visibleNodeCount: number;
  totalNodeCount: number;
}

const DEFAULT_FILTERS: FilterState = {
  languages: [],
  kinds: [],
  communityIds: [],
  edgeTypes: [],
  crossServiceOnly: false,
};

const FilterContext = createContext<FilterContextValue | null>(null);

interface FilterProviderProps {
  graphData: GraphData | null;
  children: ReactNode;
}

export function FilterProvider({ graphData, children }: FilterProviderProps) {
  const [filters, setFiltersState] = useState<FilterState>(DEFAULT_FILTERS);

  const setFilters = useCallback((newFilters: FilterState) => {
    setFiltersState(newFilters);
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  const totalNodeCount = graphData?.nodes.length ?? 0;

  const visibleNodeCount = useMemo(() => {
    if (!graphData) return 0;
    return graphData.nodes.filter((node) => {
      if (filters.languages.length > 0 && !filters.languages.includes(node.language ?? "")) {
        return false;
      }
      if (filters.kinds.length > 0 && !filters.kinds.includes(node.kind)) {
        return false;
      }
      if (filters.communityIds.length > 0 && !filters.communityIds.includes(node.community_id ?? -1)) {
        return false;
      }
      if (filters.crossServiceOnly) {
        // In cross-service mode, only show nodes that have cross-service edges
        const crossEdgeTypes = new Set(["HTTP_CALLS", "CROSS_CALLS", "EMITS", "LISTENS_ON"]);
        const hasCrossEdge = graphData.edges.some(
          (e) =>
            crossEdgeTypes.has(e.type) &&
            (e.source === node.id || e.target === node.id)
        );
        if (!hasCrossEdge) return false;
      }
      return true;
    }).length;
  }, [graphData, filters]);

  const value: FilterContextValue = {
    filters,
    setFilters,
    resetFilters,
    visibleNodeCount,
    totalNodeCount,
  };

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

export function useFilterContext(): FilterContextValue {
  const ctx = useContext(FilterContext);
  if (!ctx) {
    throw new Error("useFilterContext must be used within a FilterProvider");
  }
  return ctx;
}

export { DEFAULT_FILTERS };
